import * as React from "react";
import { Sheet, SheetContent, SheetHeader } from "@/components/ui/sheet";
import { KnowledgeBaseSideProvider } from "@/components/Shared/KnowledgeBase/KnowledgeBaseSideContext";

interface Props {
	open: boolean;
	onClose: () => void;
	/** Header content, rendered inside the bordered SheetHeader. */
	header: React.ReactNode;
	/** Optional footer bar pinned below the scrollable body. */
	footer?: React.ReactNode;
	children: React.ReactNode;
}

/**
 * The shared chrome for every filter sidebar: a right-side Radix sheet with a
 * bordered header, a scrollable body, and an optional footer bar. The list
 * filter panel and the org-filter editor render their own header/body/footer
 * into this frame so the sheet markup lives in one place.
 */
const FilterSheetFrame: React.FC<Props> = ({ open, onClose, header, footer, children }) => (
	<Sheet
		open={open}
		onOpenChange={(isOpen) => {
			if (!isOpen) onClose();
		}}
	>
		{/* gap-0 cancels SheetContent's default flex `gap-4` so the header's
		    bottom border sits flush against the first filter section — the
		    header/body and body/footer separation is handled by their borders. */}
		<SheetContent side="right" className="w-full gap-0 sm:max-w-md" aria-describedby={undefined}>
			{/* This sheet owns the right edge, so help triggers inside it open the
			    knowledge base on the left to sit alongside rather than cover it. */}
			<KnowledgeBaseSideProvider value="left">
				<SheetHeader className="border-b border-border">{header}</SheetHeader>
				<div className="flex-1 overflow-y-auto">{children}</div>
				{footer && <div className="border-t border-border px-6 py-4">{footer}</div>}
			</KnowledgeBaseSideProvider>
		</SheetContent>
	</Sheet>
);

export default FilterSheetFrame;
